<template>
  <div class="ctc-panel">
    <!-- Limits as two small gauges: what is used, what is left. -->
    <div class="ctc-panel__gauges">
      <div class="ctc-panel__gauge">
        <span>
          <strong>{{ groups.length }}</strong> / {{ limits.models }} modèles
        </span>
        <span class="ctc-panel__track" aria-hidden="true">
          <i :style="{ width: percent(groups.length, limits.models) }" />
        </span>
      </div>
      <div class="ctc-panel__gauge">
        <span>
          <strong>{{ productCount }}</strong> / {{ limits.products }} produits
        </span>
        <span class="ctc-panel__track" aria-hidden="true">
          <i :style="{ width: percent(productCount, limits.products) }" />
        </span>
      </div>
    </div>

    <article
      v-for="(group, index) in groups"
      :key="group.products[0].productLocalizedId"
      class="ctc-panel__model"
      :class="{ 'ctc-panel__model--active': index === activeIndex }"
      :aria-label="`Modèle ${index + 1}`"
      @click="$emit('select', index)"
    >
      <header class="ctc-panel__head">
        <span class="ctc-panel__num" aria-hidden="true">{{ index + 1 }}</span>
        <!-- The title is edited in place: it is the tab name in a BOX. -->
        <input
          class="ctc-panel__title"
          :value="group.title"
          maxlength="255"
          :title="group.title"
          :aria-label="`${titleLabel}, modèle ${index + 1}`"
          @click.stop="$emit('select', index)"
          @input="$emit('rename-group', { index, title: $event.target.value })"
        />
        <rmr-icon-button
          :icon="icons.mdiDeleteOutline"
          variant="neutral"
          :aria-label="`Retirer le modèle ${index + 1} et ses produits`"
          @click.stop="$emit('remove-group', index)"
        />
      </header>

      <ul class="ctc-panel__rows">
        <li
          v-for="variant in group.products"
          :key="variant.productLocalizedId"
          class="ctc-panel__row"
        >
          <span class="ctc-panel__mini">
            <img
              v-if="variant.imageUrl"
              :src="variant.imageUrl"
              alt=""
              width="36"
              height="36"
              loading="lazy"
            />
            <v-icon v-else small :color="colors.subtle">
              {{ icons.mdiImageOffOutline }}
            </v-icon>
          </span>
          <span class="ctc-panel__info">
            <span class="ctc-panel__name" :title="variant.title">
              {{ variant.title }}
            </span>
            <small>
              {{ priceLabel(variant) }} · {{ offerLabel(variant.offerCount) }}
            </small>
          </span>
          <rmr-icon-button
            :icon="icons.mdiClose"
            variant="neutral"
            size="xs"
            :aria-label="`Retirer ${variant.title} du widget`"
            @click.stop="$emit('remove-product', variant.productLocalizedId)"
          />
        </li>
      </ul>

      <footer class="ctc-panel__foot">
        <button
          type="button"
          class="ctc-panel__link"
          :disabled="!canAddVariant"
          :aria-label="`Ajouter un coloris au modèle ${index + 1}`"
          @click.stop="$emit('add-variant', index)"
        >
          <v-icon x-small color="currentColor">
            {{ icons.mdiPaletteOutline }}
          </v-icon>
          Ajouter un coloris
        </button>
        <small>{{ countOf(group) }}</small>
      </footer>
    </article>

    <p
      v-for="slot in emptySlots"
      :key="`empty-${slot}`"
      class="ctc-panel__empty"
    >
      <strong>Modèle {{ slot }} — vide</strong>
      Utilisez « Nouveau modèle » dans le menu ▾ d’un résultat.
    </p>
  </div>
</template>

<script>
import { computed } from "@vue/composition-api";
import {
  mdiClose,
  mdiDeleteOutline,
  mdiImageOffOutline,
  mdiPaletteOutline,
} from "@mdi/js";

import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import { RMR_TEXT_SUBTLE } from "@/styles/tokens";

import { WIDGET_MAX_MODELS, WIDGET_MAX_PRODUCTS } from "../constants";
import { offerCountLabel, similarPriceLabel } from "../ctc-model";

/** "Votre widget": the selected models, their colours and the limits. */
export default {
  name: "CtcSimilarGroupsEditor",
  components: { RmrIconButton },
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    canAddVariant: {
      type: Boolean,
      default: true,
    },
    // Same groups in both formats: a BOX shows each one as a tab, a CARD
    // receives a flat product list.
    widgetType: {
      type: String,
      default: "BOX",
    },
    // The model that the results' main button adds to.
    activeIndex: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const productCount = computed(() =>
      props.groups.reduce((sum, group) => sum + group.products.length, 0)
    );
    return {
      colors: { subtle: RMR_TEXT_SUBTLE },
      countOf: (group) => {
        const count = group.products.length;
        if (props.widgetType === "CARD") {
          return `${count} produit${count > 1 ? "s" : ""}`;
        }
        return `${count} coloris`;
      },
      emptySlots: computed(() =>
        Array.from(
          { length: Math.max(0, WIDGET_MAX_MODELS - props.groups.length) },
          (_, index) => props.groups.length + index + 1
        )
      ),
      icons: {
        mdiClose,
        mdiDeleteOutline,
        mdiImageOffOutline,
        mdiPaletteOutline,
      },
      limits: { models: WIDGET_MAX_MODELS, products: WIDGET_MAX_PRODUCTS },
      offerLabel: offerCountLabel,
      percent: (value, max) => `${Math.min(100, (value / max) * 100)}%`,
      priceLabel: similarPriceLabel,
      productCount,
      titleLabel: computed(() =>
        props.widgetType === "CARD"
          ? "Nom du modèle (non affiché dans la carte)"
          : "Titre de l’onglet (sans le coloris)"
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-panel__gauges {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 10px 0 16px;
}

.ctc-panel__gauge {
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;

  strong {
    color: $rmr-text-primary;
  }
}

.ctc-panel__track {
  display: block;
  height: 5px;
  margin-top: 5px;
  overflow: hidden;
  border-radius: $rmr-radius-pill;
  background: $rmr-border;

  i {
    display: block;
    height: 100%;
    border-radius: $rmr-radius-pill;
    background: $rmr-indigo;
  }
}

.ctc-panel__model {
  margin-bottom: 10px;
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-lg;
  background: $rmr-surface-0;
  cursor: pointer;
}

// The model the results' main button adds to.
.ctc-panel__model--active {
  border-color: rgba($rmr-indigo, 0.45);
  box-shadow: 0 0 0 3px $rmr-indigo-bg;
}

.ctc-panel__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 8px 8px 12px;
}

.ctc-panel__num {
  display: grid;
  width: 22px;
  height: 22px;
  flex: none;
  place-items: center;
  border-radius: $rmr-radius-sm;
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
  font-size: $rmr-fs-md;
  font-weight: 700;
}

.ctc-panel__title {
  min-width: 0;
  flex: 1;
  padding: 5px 7px;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: $rmr-radius-sm;
  background: transparent;
  color: $rmr-text-primary;
  font-size: $rmr-fs-body;
  font-weight: 600;
  text-overflow: ellipsis;
  outline: 0;

  &:hover {
    border-color: $rmr-border-hover;
    background: $rmr-surface-2;
  }

  &:focus {
    border-color: $rmr-indigo;
    background: $rmr-surface-0;
    box-shadow: 0 0 0 3px $rmr-indigo-bg;
  }
}

.ctc-panel__rows {
  margin: 0;
  padding: 0;
  border-top: 1px solid $rmr-border;
  list-style: none;
}

.ctc-panel__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 8px 12px;
}

.ctc-panel__row + .ctc-panel__row {
  border-top: 1px dashed $rmr-border;
}

.ctc-panel__mini {
  display: grid;
  width: 36px;
  height: 36px;
  flex: none;
  place-items: center;
  overflow: hidden;
  border-radius: $rmr-radius-sm;
  background: $rmr-surface-2;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.ctc-panel__info {
  min-width: 0;
  flex: 1;

  small {
    display: block;
    color: $rmr-text-muted;
    font-size: $rmr-fs-base;
  }
}

.ctc-panel__name {
  display: block;
  overflow: hidden;
  color: $rmr-text-primary;
  font-size: $rmr-fs-md;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-panel__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 6px 10px;
  border-top: 1px solid $rmr-border;
  background: $rmr-surface-2;

  small {
    color: $rmr-text-muted;
    font-size: $rmr-fs-base;
  }
}

.ctc-panel__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: 0;
  border-radius: $rmr-radius-xs;
  background: transparent;
  color: $rmr-indigo;
  font-size: $rmr-fs-md;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: $rmr-indigo-bg;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid $rmr-indigo;
    outline-offset: 2px;
  }
}

// Destructive icons stay quiet until pointed at.
.ctc-panel ::v-deep .rmr-icon-btn {
  background: transparent !important;
  color: $rmr-text-subtle !important;

  .v-icon {
    color: inherit !important;
  }

  &:hover,
  &:focus-visible {
    background: $rmr-red-bg !important;
    color: $rmr-danger !important;
  }
}

.ctc-panel__empty {
  margin: 0 0 10px;
  padding: 14px;
  border: 1.5px dashed $rmr-border-hover;
  border-radius: $rmr-radius-lg;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-md;
  text-align: center;

  strong {
    display: block;
    margin-bottom: 2px;
    color: $rmr-text-muted;
    font-size: $rmr-fs-body;
  }
}
</style>
